import React, { useEffect, useState } from 'react';
import { useAppContext } from '@prisjakt/ui-app-context';
import { useOfferListFilterContext } from 'src/areas/product/components/offer-list-filter-context';
import {
  CaptionText,
  H3Text,
  FontScale,
  FontWeight,
} from '@prisjakt/ui/text/variants';
import PriceInformation from '../../price-information';
import { sortOffersByPrice } from 'src/components/price-list/sort-by-price';
import PriceListHeader from './price-list-header';
import PriceListFooter from './price-list-footer';
import PriceListBody from './price-list-body';
import { IListPrices, IPriceListPanelProps } from './types';
import {
  getCommonListProps,
  sortByFastestDelivery,
} from '../../../../../components/price-list/sort-by-fastest-delivery';
import { getSplitOfferlist, getListLimits } from './utils/split-list';
import MissingShop from '../../missing-shop';
import OfferListEmptyMessage from '../offer-list-empty-message';
import { OptionTypeEnum } from 'src/components/filter-dropdown';
import { useProductContext } from '../../product-context';
import { PricePanel, HeaderWrapper } from './styles';

export const PriceListPanel = (props: IPriceListPanelProps) => {
  const { me, showProductInformationTexts, storeStatistics } = props;
  const { t } = useAppContext();
  const { getActiveFilters, getPriceDisplay } = useOfferListFilterContext();
  const {
    offers,
    allOffers,
    allOffersWithActiveAdditives,
  } = useProductContext();
  const [showMorePrices, setShowMorePrices] = useState(false);
  const [showMoreInSecondaryList, setShowMoreInSecondaryList] = useState(false);
  const [currentSorting, setCurrentSorting] = useState<OptionTypeEnum>(
    OptionTypeEnum.CHEAPEST_PRICE
  );
  const [sortedOffers, setSortedOffers] = useState<IListPrices>({
    mainList: offers.filteredOffers,
    secondaryList: [],
  });

  useEffect(() => {
    getOffersList();
  }, [currentSorting, offers]);

  const getOffersList = () => {
    const offersSorted = sortOffersByPrice(
      getPriceDisplay(),
      offers.filteredOffers
    );
    const offerList = getSplitOfferlist(offersSorted, getActiveFilters());
    const newSortedOffers = sortByFastestDelivery(
      offerList,
      currentSorting,
      getActiveFilters()
    );

    if (newSortedOffers !== sortedOffers) {
      setSortedOffers(newSortedOffers);
    }
  };

  const commonListProps = getCommonListProps(props);
  const listLimits = getListLimits(sortedOffers);
  const secondaryListHeaderText = `${t('product', 'splitlist-other.title')} 
    (${sortedOffers.secondaryList.length})`;

  return (
    <PricePanel>
      <PriceListHeader
        onSort={option => {
          if (!option.type || option.type === currentSorting) {
            return;
          }
          setCurrentSorting(option.type);
        }}
        sorting={currentSorting}
        allOffers={allOffers}
        allOffersWithActiveAdditives={allOffersWithActiveAdditives}
      />
      {me?.analyticsAccess?.stores && <MissingShop />}

      {sortedOffers.mainList.length ? (
        <PriceListBody
          isPrimaryList
          sortedPrices={sortedOffers.mainList}
          showMorePrices={showMorePrices}
          numberOfVisiblePrices={listLimits.mainListLimit}
          currentSorting={currentSorting}
          {...commonListProps}
        />
      ) : (
        <OfferListEmptyMessage />
      )}
      <PriceListFooter
        showProductInformationTexts={showProductInformationTexts}
        sortedPrices={sortedOffers.mainList}
        showMorePrices={showMorePrices}
        numberOfVisiblePrices={listLimits.mainListLimit}
        setShowMorePrices={setShowMorePrices}
      />
      {sortedOffers.secondaryList.length > 0 && (
        <>
          <HeaderWrapper>
            <H3Text>{secondaryListHeaderText}</H3Text>
            <CaptionText scale={FontScale.S} weight={FontWeight.HEAVIER}>
              {t('product', 'splitlist-other.description')}
            </CaptionText>
          </HeaderWrapper>
          <PriceListBody
            isPrimaryList={false}
            sortedPrices={sortedOffers.secondaryList!}
            showMorePrices={showMoreInSecondaryList}
            numberOfVisiblePrices={listLimits.secondaryListLimit}
            currentSorting={currentSorting}
            {...commonListProps}
          />
          <PriceListFooter
            showProductInformationTexts={showProductInformationTexts}
            sortedPrices={sortedOffers.secondaryList!}
            showMorePrices={showMoreInSecondaryList}
            numberOfVisiblePrices={listLimits.secondaryListLimit}
            setShowMorePrices={setShowMoreInSecondaryList}
          />
        </>
      )}

      {storeStatistics && (
        <PriceInformation storeStatistics={storeStatistics} />
      )}
    </PricePanel>
  );
};
